import { CalendarCheck, FileText, Stethoscope, Heart } from 'lucide-react';
import { WHATSAPP_URL_PROC } from '../constants';
import { useScrollReveal } from '../hooks/useScrollReveal';

const steps = [
{
  number: '01',
  icon: CalendarCheck,
  title: 'Consulta Especializada',
  desc: 'Avaliação clínica completa, explicação sobre a condição, alinhamento de expectativas e planejamento personalizado.',
  detail: 'Duração: 30-40 minutos',
  result: 'Diagnóstico confirmado e avaliação da indicação cirúrgica',
  color: '#0D47A1'
},
{
  number: '02',
  icon: FileText,
  title: 'Pré-Operatório',
  desc: 'Realização de exames pré-operatórios e consulta com anestesista enquanto aguarda a autorização do plano de saúde ou orçamento particular.',
  detail: 'Instruções sobre suspensão de medicações e tempo de jejum',
  result: 'Tudo pronto para a cirurgia',
  color: '#00897B'
},
{
  number: '03',
  icon: Stethoscope,
  title: 'Simpatectomia Torácica',
  desc: 'Você dorme sob acompanhamento de um anestesista experiente. Duas pequenas incisões, câmera de alta definição, secção das fibras nervosas responsáveis pelo suor.',
  detail: 'Duração: 30-60 minutos',
  result: 'Você já acorda sem o problema',
  color: '#0D47A1'
},
{
  number: '04',
  icon: Heart,
  title: 'Recuperação Rápida',
  desc: 'Alta hospitalar pode ocorrer no mesmo dia ou na manhã seguinte. Sem restrições definitivas após algumas semanas.',
  detail: '3-5 dias para trabalho administrativo',
  result: 'Retorno ao trabalho ou escola em dias',
  color: '#00897B'
}];


export default function ProcedureSection() {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section id="procedimento" ref={ref} className={`py-24 md:py-32 bg-white transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">

        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center bg-[#00897B]/10 text-[#00897B] rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            Como Funciona
          </div>
          <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-[#212121] mb-6 leading-tight">
            Como Funciona o Tratamento
          </h2>
          <p className="text-base md:text-lg text-[#212121]/70 leading-relaxed">
            A simpatectomia torácica é um procedimento cirúrgico minimamente invasivo que remove as fibras
            nervosas simpáticas responsáveis pelo suor excessivo.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <div
                key={i}
                data-testid={`procedure-step-${i + 1}`}
                className="relative bg-[#F5F5F5] rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">

                {/* Connector line (desktop) */}
                {i < steps.length - 1 &&
                <div className="hidden lg:block absolute top-10 -right-3 w-6 h-0.5 bg-gray-200 z-10" />
                }

                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${step.color}15` }}>

                    <Icon size={20} style={{ color: step.color }} />
                  </div>
                  <span
                    className="font-poppins font-bold text-2xl"
                    style={{ color: `${step.color}30` }}>

                    {step.number}
                  </span>
                </div>

                <h3 className="font-poppins font-bold text-lg text-[#212121] mb-2">
                  {step.title}
                </h3>
                <p className="text-[#212121]/65 text-sm leading-relaxed mb-4">
                  {step.desc}
                </p>

                <div className="border-t border-gray-200 pt-3 space-y-1">
                  <div className="text-xs text-[#212121]/50 font-medium">{step.detail}</div>
                  <div
                    className="text-xs font-semibold"
                    style={{ color: step.color }}>

                    {step.result}
                  </div>
                </div>
              </div>);

          })}
        </div>

        {/* Comparison Table */}
        <div className="mb-12 overflow-x-auto">
          <h3 className="font-poppins font-bold text-xl text-[#212121] mb-6 text-center">
            Comparativo de Tratamentos para Hiperidrose
          </h3>

          <div className="min-w-[700px] rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
            {/* Header Row */}
            <div className="grid grid-cols-5 bg-[#F5F5F5]">
              <div className="p-4 border-r border-gray-200" />
              {/* Simpatectomia — destaque */}
              <div className="p-4 bg-[#0D47A1] text-white text-center border-r border-blue-700">
                <div className="font-poppins font-bold text-base">Simpatectomia</div>
                <div className="text-blue-200 text-xs mt-0.5">Cirurgia definitiva</div>
                <span className="inline-block mt-2 bg-[#00897B] text-white text-xs font-semibold px-3 py-0.5 rounded-full">recomendado</span>
              </div>
              <div className="p-4 text-center border-r border-gray-200">
                <div className="font-poppins font-semibold text-[#212121] text-sm">Botox</div>
                <div className="text-[#212121]/50 text-xs mt-0.5">Toxina botulínica</div>
              </div>
              <div className="p-4 text-center border-r border-gray-200">
                <div className="font-poppins font-semibold text-[#212121] text-sm">Medicações</div>
                <div className="text-[#212121]/50 text-xs mt-0.5">Anticolinérgicos</div>
              </div>
              <div className="p-4 text-center">
                <div className="font-poppins font-semibold text-[#212121] text-sm">Desodorante</div>
                <div className="text-[#212121]/50 text-xs mt-0.5">Antitranspirante</div>
              </div>
            </div>

            {/* Row 1 — Custo inicial */}
            <div className="grid grid-cols-5 border-t border-gray-200">
              <div className="p-4 bg-white border-r border-gray-200 flex items-center">
                <span className="text-[#212121]/70 text-sm font-medium">Custo inicial</span>
              </div>
              <div className="p-4 bg-[#EEF4FF] border-r border-blue-100 text-center">
                <span className="inline-block bg-[#00897B]/15 text-[#00897B] text-xs font-semibold px-3 py-1 rounded-full mb-1">Coberto pelo convênio</span>
                <div className="text-[#212121]/50 text-xs">cirurgia única<br/>sem desembolso recorrente</div>
              </div>
              <div className="p-4 bg-white border-r border-gray-200 text-center">
                <div className="text-[#212121]/80 text-sm font-semibold">R$ 1.500 – 3.000</div>
                <div className="text-[#212121]/45 text-xs">por sessão</div>
              </div>
              <div className="p-4 bg-white border-r border-gray-200 text-center">
                <div className="text-[#212121]/80 text-sm font-semibold">R$ 80 – 250</div>
                <div className="text-[#212121]/45 text-xs">por mês</div>
              </div>
              <div className="p-4 bg-white text-center">
                <div className="text-[#212121]/80 text-sm font-semibold">R$ 20 – 80</div>
                <div className="text-[#212121]/45 text-xs">por mês</div>
              </div>
            </div>

            {/* Row 2 — Custo em 5 anos */}
            <div className="grid grid-cols-5 border-t border-gray-200">
              <div className="p-4 bg-white border-r border-gray-200 flex items-center">
                <span className="text-[#212121]/70 text-sm font-medium">Custo em 5 anos</span>
              </div>
              <div className="p-4 bg-[#EEF4FF] border-r border-blue-100 text-center">
                <span className="inline-block bg-[#00897B]/15 text-[#00897B] text-xs font-semibold px-3 py-1 rounded-full mb-1">Menor custo</span>
                <div className="text-[#212121]/50 text-xs">sem reaplicações</div>
              </div>
              <div className="p-4 bg-white border-r border-gray-200 text-center">
                <span className="inline-block bg-red-100 text-red-600 text-xs font-semibold px-2 py-0.5 rounded-full mb-1">R$ 15.000 – 30.000+</span>
                <div className="text-[#212121]/45 text-xs">10+ sessões</div>
              </div>
              <div className="p-4 bg-white border-r border-gray-200 text-center">
                <span className="inline-block bg-orange-100 text-orange-600 text-xs font-semibold px-2 py-0.5 rounded-full mb-1">R$ 5.000 – 15.000</span>
                <div className="text-[#212121]/45 text-xs">uso contínuo</div>
              </div>
              <div className="p-4 bg-white text-center">
                <span className="inline-block bg-blue-100 text-blue-600 text-xs font-semibold px-2 py-0.5 rounded-full mb-1">R$ 1.200 – 5.000</span>
                <div className="text-[#212121]/45 text-xs">uso contínuo</div>
              </div>
            </div>

            {/* Row 3 — Reaplicação */}
            <div className="grid grid-cols-5 border-t border-gray-200">
              <div className="p-4 bg-white border-r border-gray-200 flex items-center">
                <span className="text-[#212121]/70 text-sm font-medium">Reaplicação necessária</span>
              </div>
              <div className="p-4 bg-[#EEF4FF] border-r border-blue-100 text-center">
                <div className="text-[#00897B] font-bold text-base">✗ Não</div>
                <div className="text-[#212121]/50 text-xs">procedimento único</div>
              </div>
              <div className="p-4 bg-white border-r border-gray-200 text-center">
                <div className="text-red-500 font-bold text-base">✓</div>
                <div className="text-[#212121]/45 text-xs">a cada 6–8 meses</div>
              </div>
              <div className="p-4 bg-white border-r border-gray-200 text-center">
                <div className="text-red-500 font-bold text-base">✓</div>
                <div className="text-[#212121]/45 text-xs">uso diário contínuo</div>
              </div>
              <div className="p-4 bg-white text-center">
                <div className="text-red-500 font-bold text-base">✓</div>
                <div className="text-[#212121]/45 text-xs">aplicação diária</div>
              </div>
            </div>

            {/* Row 4 — Eficácia */}
            <div className="grid grid-cols-5 border-t border-gray-200">
              <div className="p-4 bg-white border-r border-gray-200 flex items-center">
                <span className="text-[#212121]/70 text-sm font-medium">Eficácia</span>
              </div>
              <div className="p-4 bg-[#EEF4FF] border-r border-blue-100 text-center">
                <span className="inline-block bg-[#00897B]/15 text-[#00897B] text-xs font-semibold px-3 py-1 rounded-full mb-1">Excelente</span>
                <div className="text-[#212121]/50 text-xs">95–98% dos casos</div>
              </div>
              <div className="p-4 bg-white border-r border-gray-200 text-center">
                <span className="inline-block bg-blue-100 text-blue-600 text-xs font-semibold px-2 py-0.5 rounded-full mb-1">Boa</span>
                <div className="text-[#212121]/45 text-xs">enquanto dura o efeito</div>
              </div>
              <div className="p-4 bg-white border-r border-gray-200 text-center">
                <span className="inline-block bg-orange-100 text-orange-600 text-xs font-semibold px-2 py-0.5 rounded-full mb-1">Parcial</span>
                <div className="text-[#212121]/45 text-xs">efeitos colaterais comuns</div>
              </div>
              <div className="p-4 bg-white text-center">
                <span className="inline-block bg-red-100 text-red-500 text-xs font-semibold px-2 py-0.5 rounded-full mb-1">Baixa</span>
                <div className="text-[#212121]/45 text-xs">casos leves apenas</div>
              </div>
            </div>

            {/* Row 5 — Duração */}
            <div className="grid grid-cols-5 border-t border-gray-200">
              <div className="p-4 bg-white border-r border-gray-200 flex items-center">
                <span className="text-[#212121]/70 text-sm font-medium">Duração do resultado</span>
              </div>
              <div className="p-4 bg-[#EEF4FF] border-r border-blue-100 text-center">
                <div className="text-[#0D47A1] font-poppins font-bold text-sm">Permanente</div>
                <div className="text-[#212121]/50 text-xs">resultado definitivo</div>
              </div>
              <div className="p-4 bg-white border-r border-gray-200 text-center">
                <div className="text-[#212121]/80 text-sm font-semibold">6–8 meses</div>
                <div className="text-[#212121]/45 text-xs">necessita repetição</div>
              </div>
              <div className="p-4 bg-white border-r border-gray-200 text-center">
                <div className="text-[#212121]/80 text-sm font-semibold">Enquanto usar</div>
                <div className="text-[#212121]/45 text-xs">sem melhora a longo prazo</div>
              </div>
              <div className="p-4 bg-white text-center">
                <div className="text-[#212121]/80 text-sm font-semibold">Horas</div>
                <div className="text-[#212121]/45 text-xs">efeito superficial</div>
              </div>
            </div>

            {/* Row 6 — Qualidade de vida */}
            <div className="grid grid-cols-5 border-t border-gray-200">
              <div className="p-4 bg-white border-r border-gray-200 flex items-center">
                <span className="text-[#212121]/70 text-sm font-medium">Qualidade de vida</span>
              </div>
              <div className="p-4 bg-[#EEF4FF] border-r border-blue-100 text-center">
                <div className="text-yellow-500 text-lg">★★★★★</div>
                <div className="text-[#212121]/50 text-xs">transformação completa</div>
              </div>
              <div className="p-4 bg-white border-r border-gray-200 text-center">
                <div className="text-lg"><span className="text-yellow-500">★★★</span><span className="text-gray-300">★★</span></div>
                <div className="text-[#212121]/45 text-xs">melhora temporária</div>
              </div>
              <div className="p-4 bg-white border-r border-gray-200 text-center">
                <div className="text-lg"><span className="text-yellow-500">★★</span><span className="text-gray-300">★★★</span></div>
                <div className="text-[#212121]/45 text-xs">limitado por efeitos</div>
              </div>
              <div className="p-4 bg-white text-center">
                <div className="text-lg"><span className="text-yellow-500">★</span><span className="text-gray-300">★★★★</span></div>
                <div className="text-[#212121]/45 text-xs">impacto mínimo</div>
              </div>
            </div>

            {/* Row 7 — Praticidade */}
            <div className="grid grid-cols-5 border-t border-gray-200">
              <div className="p-4 bg-white border-r border-gray-200 flex items-center">
                <span className="text-[#212121]/70 text-sm font-medium">Praticidade</span>
              </div>
              <div className="p-4 bg-[#EEF4FF] border-r border-blue-100 text-center">
                <span className="inline-block bg-[#00897B]/15 text-[#00897B] text-xs font-semibold px-3 py-1 rounded-full mb-1">Máxima</span>
                <div className="text-[#212121]/50 text-xs">sem rotina de tratamento</div>
              </div>
              <div className="p-4 bg-white border-r border-gray-200 text-center">
                <span className="inline-block bg-orange-100 text-orange-600 text-xs font-semibold px-2 py-0.5 rounded-full mb-1">Moderada</span>
                <div className="text-[#212121]/45 text-xs">consultas frequentes</div>
              </div>
              <div className="p-4 bg-white border-r border-gray-200 text-center">
                <span className="inline-block bg-red-100 text-red-500 text-xs font-semibold px-2 py-0.5 rounded-full mb-1">Baixa</span>
                <div className="text-[#212121]/45 text-xs">uso diário obrigatório</div>
              </div>
              <div className="p-4 bg-white text-center">
                <span className="inline-block bg-orange-100 text-orange-600 text-xs font-semibold px-2 py-0.5 rounded-full mb-1">Simples</span>
                <div className="text-[#212121]/45 text-xs">mas ineficaz para hiperidrose</div>
              </div>
            </div>
          </div>

          {/* Summary box */}
          <div className="mt-4 bg-[#EEF4FF] border border-[#0D47A1]/20 rounded-2xl p-5">
            <p className="text-[#0D47A1] font-poppins font-semibold text-sm mb-1">Por que a simpatectomia é a melhor opção?</p>
            <p className="text-[#212121]/70 text-sm leading-relaxed">
              Único tratamento que elimina definitivamente a hiperidrose com uma única intervenção — sem rotinas diárias, sem reaplicações e com o menor custo total ao longo dos anos.
            </p>
          </div>
        </div>

        <div className="text-center">
          <a
            href={WHATSAPP_URL_PROC}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="procedure-cta-button"
            className="inline-flex items-center gap-2 bg-[#0D47A1] text-white font-poppins font-bold px-10 py-4 rounded-full hover:bg-[#00897B] transition-colors duration-200 shadow-lg hover:-translate-y-0.5 hover:shadow-xl">QUERO SABER MAIS


          </a>
        </div>

      </div>
    </section>);

}